import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ContactForm from './ContactForm';

const { FiMail, FiMapPin, FiClock, FiCalendar } = FiIcons;

const CTA = ({ prefillMessage }) => {
  return (
    <section
      id="contact"
      className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-stone-50 to-amber-50"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2
            className="text-4xl md:text-5xl font-serif text-stone-800 mb-6"
            style={{ fontFamily: 'Libre Baskerville,serif' }}
          >
            Bereit für deine Entspannungsreise?
          </h2>
          <div className="max-w-3xl mx-auto mb-8">
            <p
              className="text-lg text-stone-600 mb-4"
              style={{ fontFamily: 'Lato,sans-serif' }}
            >
              Wenn du dir einen Platz sichern möchtest, fülle das Formular aus.
              <br />
              Es entsteht keine Verpflichtung und keine Zahlung an dieser Stelle.
            </p>
            <p
              className="text-sm text-stone-500 italic flex items-center justify-center gap-2"
              style={{ fontFamily: 'Lato,sans-serif' }}
            >
              <SafeIcon icon={FiCalendar} className="w-4 h-4" />
              Sollten die aktuellen Termine für dich nicht passen, melde dich
              bitte über unser Anmeldeformular.
            </p>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="p-8 rounded-2xl shadow-lg border max-w-4xl mx-auto mb-16"
            style={{ backgroundColor: '#FFFFFF', borderColor: '#CBB8A9' }}
          >
            <p
              className="text-stone-800 leading-relaxed text-center"
              style={{ fontFamily: 'Lato,sans-serif' }}
            >
              <span
                className="block font-semibold mb-3 text-lg"
                style={{
                  fontFamily: 'Libre Baskerville,serif',
                  color: '#314C40',
                }}
              >
                Katja, Teilnehmerin:
              </span>
              <span className="italic text-base" style={{ color: '#2C2C2C' }}>
                „Mein Wunsch war es, bewusst nichts leisten zu müssen, tief zu
                entspannen und mir Zeit nur für mich selbst zu nehmen. Entgegen
                meiner Erwartung konnte ich überraschend schnell loslassen und
                in einen sehr tiefen Entspannungszustand eintauchen.“
              </span>
            </p>
          </motion.div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-stretch">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-white/70 backdrop-blur-sm p-10 md:p-12 rounded-2xl shadow-lg border border-stone-200 flex flex-col justify-between"
          >
            <div>
              <h3
                className="text-2xl font-serif text-stone-800 mb-10"
                style={{ fontFamily: 'Libre Baskerville,serif' }}
              >
                Kontakt und Information
              </h3>
              <div className="space-y-12">
                <div className="flex items-center">
                  <div className="w-14 h-14 bg-stone-100 rounded-full flex items-center justify-center mr-6 flex-shrink-0 shadow-sm">
                    <SafeIcon icon={FiMail} className="w-6 h-6 text-stone-700" />
                  </div>
                  <div>
                    <h4
                      className="font-bold text-stone-800 uppercase text-xs tracking-widest mb-1"
                      style={{ fontFamily: 'Lato,sans-serif' }}
                    >
                      E-Mail
                    </h4>
                    <p
                      className="text-lg text-stone-600"
                      style={{ fontFamily: 'Lato,sans-serif' }}
                    >
                      info@yin-und-klang.de
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-14 h-14 bg-stone-100 rounded-full flex items-center justify-center mr-6 flex-shrink-0 shadow-sm">
                    <SafeIcon icon={FiClock} className="w-6 h-6 text-stone-700" />
                  </div>
                  <div className="space-y-10 pt-1">
                    <div>
                      <h4
                        className="font-bold text-stone-800 text-lg mb-2"
                        style={{ fontFamily: 'Libre Baskerville,serif' }}
                      >
                        4 × 70 Minuten - Kurs
                      </h4>
                      <p
                        className="text-stone-600 mb-3"
                        style={{ fontFamily: 'Lato,sans-serif' }}
                      >
                        jeweils Dienstag, 18:30 Uhr bis 19:40 Uhr
                        <br />
                        03. November 2026
                        <br />
                        10. November 2026
                        <br />
                        17. November 2026
                        <br />
                        24. November 2026
                      </p>
                      <div className="flex items-start text-stone-500 text-sm leading-relaxed p-3 bg-stone-50/50 rounded-lg border border-stone-100">
                        <SafeIcon
                          icon={FiMapPin}
                          className="w-4 h-4 mr-2 mt-1 flex-shrink-0"
                        />
                        <span>
                          Fairyflorals - Flowers &amp; Flow
                          <br />
                          Wolfhager Straße 409
                          <br />
                          34128 Kassel
                        </span>
                      </div>
                    </div>

                    <div>
                      <h4
                        className="font-bold text-stone-800 text-lg mb-2"
                        style={{ fontFamily: 'Libre Baskerville,serif' }}
                      >
                        3 Stunden Retreat
                      </h4>
                      <p
                        className="text-stone-600 mb-3"
                        style={{ fontFamily: 'Lato,sans-serif' }}
                      >
                        jeweils 14:00 Uhr bis 17:00 Uhr
                        <br />
                        <span className="block font-medium mt-2 text-stone-800">
                          Samstag, 14. November 2026
                        </span>
                        <span className="block font-medium mt-1 text-stone-800">
                          Sonntag, 13. Dezember 2026
                        </span>
                        <span className="block font-medium mt-1 text-stone-800">
                          Samstag, 16. Januar 2027
                        </span>
                        <span className="block font-medium mt-1 text-stone-800">
                          Sonntag, 21. Februar 2027
                        </span>
                        <span className="block text-sm text-stone-500 mt-2">
                          Zukünftig: weitere Retreat Termine werden laufend
                          bekannt gegeben.
                        </span>
                      </p>
                      <div className="space-y-3">
                        <div className="flex items-start text-stone-500 text-sm leading-relaxed p-3 bg-stone-50/50 rounded-lg border border-stone-100">
                          <SafeIcon
                            icon={FiMapPin}
                            className="w-4 h-4 mr-2 mt-1 flex-shrink-0"
                          />
                          <span>
                            VITAL CENTER by Alex
                            <br />
                            Sophienstraße 32 - 3. Etage
                            <br />
                            34117 Kassel
                          </span>
                        </div>
                        <div className="flex items-start text-stone-500 text-sm leading-relaxed p-3 bg-stone-50/50 rounded-lg border border-stone-100">
                          <SafeIcon
                            icon={FiMapPin}
                            className="w-4 h-4 mr-2 mt-1 flex-shrink-0"
                          />
                          <span>
                            Fairyflorals - Flowers &amp; Flow
                            <br />
                            Wolfhager Straße 409
                            <br />
                            34128 Kassel
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-12 pt-8 border-t border-stone-100">
              <p
                className="text-sm text-stone-400 italic text-center"
                style={{ fontFamily: 'Lato,sans-serif' }}
              >
                Wir freuen uns darauf, dich auf deiner Reise zu begleiten.
              </p>
            </div>
          </motion.div>

          <div className="flex flex-col">
            <ContactForm prefillMessage={prefillMessage} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTA;